import { SkeletonLoader } from '@/components/ui/SkeletonLoader';
import SimpleSelect, { SelectOption } from '@/components/ui/SimpleSelect';
import { aiModelSettingsService } from '@/services/ai-model-settings.service';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Award, BookOpen, Brain, FileSearch, GitBranch, Save, TrendingUp, Info, Zap, Sparkles, DollarSign } from 'lucide-react';
import { useEffect, useState } from 'react';
import toast from 'react-hot-toast';
import usePermissionRouteAccess from '@/hooks/usePermissionRouteAccess';
import useHasPermission from '@/hooks/useHasPermission';
import { AI_MODEL_SETTINGS_PERMISSION } from '@/constants/constant-permissions';
import type {
  AIModelCatalog,
  AIModelCatalogEntry,
  AIModelSettings as AIModelSettingsType,
  ModelCategoryKey,
  ModelGroupKey,
  SaveAIModelSettings,
} from '@/types/ai-model-settings.types';

type Row = {
  field: string;
  label: string;
  description: string;
  category: ModelCategoryKey;
  // Hide "-lite" models for this dropdown. Lite tiers can't reliably produce the
  // complex nested schemas for course/exam GENERATION (runaway looping), so they're
  // excluded from those two dropdowns only — evaluation/chatbot/etc. keep lite.
  excludeLite?: boolean;
};

type Section = { title: string; icon: typeof BookOpen; group: ModelGroupKey; rows: Row[] };

// UI is grouped BY FEATURE — one card per backend object. Within each card the text
// model and its image model sit SIDE BY SIDE (2-column grid). Options per row come
// from the catalog category, so models added on the backend appear automatically.
const SECTIONS: Section[] = [
  {
    title: 'Course',
    icon: BookOpen,
    group: 'course',
    rows: [
      { field: 'generation', label: 'Course Generation', description: 'Model used to generate AI course content.', category: 'text', excludeLite: true },
      { field: 'image', label: 'Image Generation', description: 'Model for course banners, thumbnails and content images.', category: 'image' },
      { field: 'chatbot', label: 'Chatbot', description: 'Model that powers the course assistant chatbot.', category: 'text' },
      { field: 'evaluation', label: 'Answer Evaluation', description: 'Model used to evaluate course assessment text/paragraph answers.', category: 'text' },
      // Lite excluded: a translation batch must come back with exactly the
      // segments it was sent, and there is no retry loop if it does not.
      { field: 'translation', label: 'Course Translation', description: 'Model used to translate courses into other languages.', category: 'text', excludeLite: true },
    ],
  },
  {
    title: 'Certification',
    icon: Award,
    group: 'certification',
    rows: [
      { field: 'generation', label: 'Certification Exam Generation', description: 'Model used to generate certification exams.', category: 'text', excludeLite: true },
      { field: 'image', label: 'Image Generation', description: 'Model for certification banners, thumbnails.', category: 'image' },
      { field: 'evaluation', label: 'Answer Evaluation', description: 'Model used to evaluate text-answer exam questions.', category: 'text' },
    ],
  },
  {
    title: 'Learning Path',
    icon: GitBranch,
    group: 'learning_path',
    rows: [
  { field: 'generation', label: 'Learning Path Generation', description: 'Model used to generate learning paths.', category: 'text', excludeLite: true },
      { field: 'image', label: 'Image Generation', description: 'Model for learning path banners, thumbnails.', category: 'image' },
    ],
  },
  {
    title: 'Trending Topics',
    icon: TrendingUp,
    group: 'trending_topics',
    rows: [
      { field: 'discovery', label: 'Trending Topics', description: 'Model for AI-driven trending topic discovery.', category: 'text' },
    ],
  },
  {
    title: 'Document Store',
    icon: FileSearch,
    group: 'document_store',
    rows: [
      // Only `dimensions` is DB-only (not UI-editable) — changing it after documents are
      // indexed would break vector search. Embeddings, search + analysis stay selectable.
      { field: 'embeddings', label: 'Embeddings', description: 'Model used to embed documents for vector search / retrieval.', category: 'embedding' },
      { field: 'search', label: 'AI Search Enrichment', description: 'Model used to AI-enrich document store search results.', category: 'text' },
      { field: 'analysis', label: 'Document Analysis', description: 'Model used by the analyzer agent to read uploaded documents — summary, topic counts and suggested course structure.', category: 'text' },
    ],
  },
];

// First option in every dropdown — picking it clears the field (saved as null/unset).
const CLEAR_OPTION: SelectOption = { value: '', label: 'Select model' };

const toOptions = (entries?: AIModelCatalogEntry[]): SelectOption[] =>
  [CLEAR_OPTION, ...(entries || []).map((m) => ({ value: m.id, label: m.label }))];

export default function AIModelSettings() {
  usePermissionRouteAccess([
    AI_MODEL_SETTINGS_PERMISSION.ADMIN_AI_MODEL_SETTINGS_READ,
    AI_MODEL_SETTINGS_PERMISSION.ADMIN_AI_MODEL_SETTINGS_WRITE,
  ]);
  const canWrite = useHasPermission([AI_MODEL_SETTINGS_PERMISSION.ADMIN_AI_MODEL_SETTINGS_WRITE]);
  const queryClient = useQueryClient();

  // Available models (drives the dropdowns) — new backend models appear automatically.
  const { data: catalogResp, isLoading: catalogLoading } = useQuery({
    queryKey: ['aiModelCatalog'],
    queryFn: () => aiModelSettingsService.getCatalog(),
  });

  // Current tenant selection (seeded with defaults at tenant creation).
  const { data: settingsResp, isLoading: settingsLoading } = useQuery({
    queryKey: ['aiModelSettings'],
    queryFn: () => aiModelSettingsService.getSettings(),
  });

  const catalog = catalogResp?.data as AIModelCatalog | undefined;
  const settings = settingsResp?.data as AIModelSettingsType | undefined;

  const [selections, setSelections] = useState<SaveAIModelSettings>({});

  // Seed local state once the saved selection arrives.
  useEffect(() => {
    if (settings) {
      setSelections({
        course: settings.course,
        certification: settings.certification,
        learning_path: settings.learning_path,
        trending_topics: settings.trending_topics,
        document_store: settings.document_store,
      });
    }
  }, [settings]);

  const saveMutation = useMutation({
    mutationFn: (data: SaveAIModelSettings) => aiModelSettingsService.saveSettings(data),
    onSuccess: () => {
      toast.success('AI model settings saved successfully!');
      queryClient.invalidateQueries({ queryKey: ['aiModelSettings'] });
    },
    onError: (error: Error) => {
      toast.error(error.message || 'Failed to save settings.');
    },
  });

  const getValue = (group: ModelGroupKey, field: string): string | null | undefined =>
    (selections[group] as Record<string, string | null> | undefined)?.[field];

  const setField = (group: ModelGroupKey, field: string, value: string | number | null) => {
    // A real id is stored as-is; the "Select model" option (empty) clears the field to null,
    // which is sent to the backend and persisted as an unset model.
    setSelections((prev) => ({
      ...prev,
      [group]: { ...(prev[group] as Record<string, string | null> | undefined), [field]: value ? String(value) : null },
    }) as SaveAIModelSettings);
  };

  // All fields are optional — save whatever subset is selected.
  const onSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    saveMutation.mutate(selections);
  };

  const isLoading = catalogLoading || settingsLoading;

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
          <SkeletonLoader type="title" className="bg-white/20 mb-2" />
          <SkeletonLoader type="text" width="60%" className="bg-white/20" />
        </div>
        {Array.from({ length: 5 }).map((_, c) => (
          <div key={c} className="bg-white rounded-xl p-6 shadow-sm border">
            <SkeletonLoader type="title" className="mb-6" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {Array.from({ length: 2 }).map((_, i) => (
                <div key={i}>
                  <SkeletonLoader type="text" width="40%" className="mb-2" />
                  <SkeletonLoader type="custom" className="h-11 w-full rounded-lg" />
                </div>
              ))}
            </div>
          </div>
        ))}
        <div className="flex justify-end">
          <SkeletonLoader type="button" width="140px" />
        </div>
      </div>
    );
  }

  const optionsByCategory: Record<ModelCategoryKey, SelectOption[]> = {
    text: toOptions(catalog?.text),
    image: toOptions(catalog?.image),
    embedding: toOptions(catalog?.embedding),
  };

  return (
    <div className="space-y-6">
      {/* Page Header */}
      <div className="bg-gradient-to-r from-brand-indigo-600 to-brand-purple-600 rounded-xl p-6 text-white">
        <div className="flex items-center gap-2">
          <Brain className="w-6 h-6" />
          <h1 className="text-2xl font-bold">AI Model Settings</h1>
        </div>
        <p className="text-brand-indigo-100 mt-1">Choose which AI model powers each feature for your organization.</p>
      </div>

      {/* Model selection guidance — helps admins pick a cost-appropriate, stable tier. */}
      <div className="card border-l-4 border-primary-500 bg-primary-50/40">
        <div className="flex items-start gap-3 mb-4">
          <div className="p-2 bg-primary-100 rounded-lg shrink-0">
            <Info className="w-5 h-5 text-primary-600" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-gray-900">Choosing the right model</h2>
            <p className="text-sm text-gray-600 mt-0.5">
              Match the model tier to the task. The <strong>Flash</strong> tiers are the recommended,
              stable default — <strong>Pro</strong> models are noticeably more expensive and only worth it
              for the hardest tasks.
            </p>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Flash — recommended default */}
          <div className="relative rounded-xl border border-emerald-200 bg-white p-4">
            <span className="absolute top-3 right-3 text-[10px] font-semibold uppercase tracking-wide text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full">
              Recommended
            </span>
            <div className="flex items-center gap-2 mb-1">
              <Zap className="w-4 h-4 text-emerald-600" />
              <h3 className="font-semibold text-gray-900">Gemini Flash Versions</h3>
            </div>
            <p className="text-xs font-medium text-emerald-700 mb-2">Stable · balanced cost</p>
            <p className="text-sm text-gray-600">
              Best for heavy work like course, certification &amp; learning-path{' '}
              <strong>generation</strong>. The reliable default for most features.
            </p>
          </div>

          {/* Flash-Lite — light tasks */}
          <div className="rounded-xl border border-sky-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-1">
              <Sparkles className="w-4 h-4 text-sky-600" />
              <h3 className="font-semibold text-gray-900">Gemini Flash-Lite Versions</h3>
            </div>
            <p className="text-xs font-medium text-sky-700 mb-2">Lightweight · lowest cost</p>
            <p className="text-sm text-gray-600">
              Ideal for small, light tasks like <strong>enhancement</strong>, short prompts and quick
              helpers. Not recommended for complex generation.
            </p>
          </div>

          {/* Pro — expensive */}
          <div className="rounded-xl border border-amber-200 bg-white p-4">
            <div className="flex items-center gap-2 mb-1">
              <DollarSign className="w-4 h-4 text-amber-600" />
              <h3 className="font-semibold text-gray-900">Gemini Pro Versions</h3>
            </div>
            <p className="text-xs font-medium text-amber-700 mb-2">Highest quality · highest cost</p>
            <p className="text-sm text-gray-600">
              Most capable but <strong>significantly more expensive</strong>. Use only when a task truly
              needs it — Flash covers most needs.
            </p>
          </div>
        </div>

        <p className="mt-4 text-xs text-gray-500 flex items-center gap-1.5">
          <Info className="w-3.5 h-3.5 shrink-0" />
          Tip: default to Flash for stability, use Flash-Lite for small tasks to save cost, and reserve
          Pro for the hardest jobs.
        </p>
      </div>

      <form onSubmit={onSubmit} className={`space-y-6 ${saveMutation.isPending ? 'cursor-wait' : ''}`}>
        <fieldset disabled={!canWrite || saveMutation.isPending} className={`space-y-6 ${!canWrite || saveMutation.isPending ? 'opacity-60' : ''}`}>
          {SECTIONS.map((section) => {
            const SectionIcon = section.icon;
            return (
              <div key={section.group} className="card">
                <div className="flex items-center gap-2 mb-6">
                  <SectionIcon className="w-5 h-5 text-brand-indigo-600" />
                  <h2 className="text-lg font-semibold">{section.title}</h2>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  {section.rows.map((row) => {
                    // Exclude "-lite" models for generation dropdowns (see Row.excludeLite).
                    const rowOptions = row.excludeLite
                      ? optionsByCategory[row.category].filter(
                          (o) => !String(o.value).toLowerCase().includes('-lite'),
                        )
                      : optionsByCategory[row.category];
                    return (
                      <SimpleSelect
                        key={row.field}
                        label={row.label}
                        helperText={row.description}
                        options={rowOptions}
                        value={getValue(section.group, row.field) ?? undefined}
                        onChange={(v) => setField(section.group, row.field, v)}
                        isDisabled={!canWrite}
                        placeholder="Select a model..."
                      />
                    );
                  })}
                </div>
              </div>
            );
          })}
        </fieldset>

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            className={`btn btn-primary inline-flex items-center${!canWrite || saveMutation.isPending ? ' opacity-50 cursor-not-allowed' : ''}`}
            disabled={!canWrite || saveMutation.isPending}
            title={!canWrite ? 'You do not have permission' : undefined}
          >
            <Save className="w-4 h-4 mr-2" />
            {saveMutation.isPending ? 'Saving...' : 'Save Settings'}
          </button>
        </div>
      </form>
    </div>
  );
}
